/**
 * Runtime configuration.
 *
 * The same container image is deployed to every environment, so none of these
 * values may be baked in at build time. `next build` inlines `process.env.*`
 * into the client bundle, and `publicRuntimeConfig` no longer exists in Next
 * 16, so the values are plumbed through explicitly instead:
 *
 *   server  -> read `process.env` on every call (the real environment)
 *   browser -> read `window.__RUNTIME_CONFIG__`, written by a blocking
 *              <script src="/api/runtime-config"> in `_document`
 *
 * Because that script is synchronous and first in <head>, the config is
 * populated before any application bundle evaluates. Modules may therefore
 * read it at module scope (several do) and still see real values.
 *
 * Adding a value means adding it in exactly two places: `RUNTIME_CONFIG_ENV`
 * below, and the `RuntimeConfig` interface.
 */

export interface RuntimeConfig {
  baseUrl?: string;
  gtmId?: string;
  googleMapsApiKey?: string;
  maintenance?: string;
  hostedPayments?: string;
  cardinalDomain?: string;
  cardinalCollectUrl?: string;
  cardinalStepUpUrl?: string;
  basketProcessingRetryLimit?: string;
  calendarDomain?: string;
  sisenseUrl?: string;
  cookiebotId?: string;
}

/**
 * Config key -> the environment variables to read, in order of preference.
 *
 * None of these carry the `NEXT_PUBLIC_` prefix, deliberately. That prefix is
 * an instruction to `next build` to inline the value into the build output — the
 * exact behaviour that froze one environment's Google Maps token into the image.
 * Dropping it makes inlining impossible rather than merely unused, so a value
 * accidentally present on the build machine cannot leak into the artefact.
 *
 * The `NEXT_PUBLIC_*` spellings are still accepted as a fallback so existing
 * deployments keep working while their configuration is updated. They are
 * deprecated and should be removed once every environment sets the new names.
 *
 * This is also the allow-list that bounds what `/api/runtime-config` will
 * serialise into the page: every value here is readable by the browser, so only
 * add variables that are safe to make public. Server-only secrets must be read
 * from `process.env` directly, inside server-only code.
 */
export const RUNTIME_CONFIG_ENV: Record<keyof RuntimeConfig, string[]> = {
  baseUrl: ['BASE_URL', 'NEXT_PUBLIC_BASE_URL'],
  gtmId: ['GTM_ID', 'NEXT_PUBLIC_GTM_ID'],
  googleMapsApiKey: [
    'GOOGLE_MAPS_API_TOKEN',
    'NEXT_PUBLIC_GOOGLE_MAPS_API_TOKEN',
  ],
  maintenance: ['MAINTENANCE', 'NEXT_PUBLIC_MAINTENANCE'],
  hostedPayments: ['WORLDPAY_HOSTED_PAYMENTS'],
  cardinalDomain: ['WORLDPAY_CARDINAL_DOMAIN'],
  cardinalCollectUrl: ['WORLDPAY_CARDINAL_COLLECT_URL'],
  cardinalStepUpUrl: ['WORLDPAY_CARDINAL_STEP_UP_URL'],
  basketProcessingRetryLimit: [
    'BASKET_PROCESSING_RETRY_LIMIT',
    'NEXT_PUBLIC_BASKET_PROCESSING_RETRY_LIMIT',
  ],
  calendarDomain: ['CALENDAR_DOMAIN', 'NEXT_PUBLIC_CALENDAR_DOMAIN'],
  sisenseUrl: ['SISENSE_URL', 'NEXT_PUBLIC_SISENSE_URL'],
  cookiebotId: ['COOKIEBOT_ID', 'NEXT_PUBLIC_COOKIEBOT_ID'],
};

const CONFIG_KEYS = Object.keys(RUNTIME_CONFIG_ENV) as (keyof RuntimeConfig)[];

declare global {
  interface Window {
    __RUNTIME_CONFIG__?: RuntimeConfig;
  }
}

/**
 * Read the current environment into a config object. Server-side only — on the
 * client every `process.env` lookup here would have been replaced by the
 * bundler, which is exactly what we are avoiding.
 *
 * `process.env` is indexed dynamically on purpose: Next only substitutes
 * statically analysable `process.env.SOME_NAME` accesses, so going through a
 * variable keeps these reads live at runtime.
 */
export function readRuntimeConfigFromEnv(): RuntimeConfig {
  const config: RuntimeConfig = {};

  for (const key of CONFIG_KEYS) {
    for (const name of RUNTIME_CONFIG_ENV[key]) {
      const value = process.env[name];

      // Treat blank as absent: orchestrators routinely inject empty strings for
      // variables that have no value set for the environment. Also lets a
      // deployment blank out a legacy name rather than having to unset it.
      if (value !== undefined && value !== '') {
        config[key] = value;
        break;
      }
    }
  }

  return config;
}

/**
 * The runtime configuration for the current request/process.
 */
export default function getRuntimeConfig(): RuntimeConfig {
  if (typeof window === 'undefined') {
    return readRuntimeConfigFromEnv();
  }

  if (window.__RUNTIME_CONFIG__) {
    return window.__RUNTIME_CONFIG__;
  }

  // The bootstrap script has not run. In a browser that means the config is
  // genuinely unavailable and the dynamic `process.env` reads below all resolve
  // to undefined, which is the honest answer. Under jsdom it lets tests
  // configure themselves by assigning to `process.env` (see vitest.setup.ts).
  if (typeof process !== 'undefined' && process.env) {
    return readRuntimeConfigFromEnv();
  }

  return {};
}
